import type {
  PaymentsAPIResponse,
  Withdrawal,
  WithdrawalSort
} from '@library/types/payments';
import {
  getPaymentsDateTime,
  isTimeWithinInterval
} from '@library/utils/dateTime';
import { DateTime } from 'luxon';
import { PaginationButtons } from '@library/components/buttons';
import { SELECT_ALL } from '@library/component-utils/filters';
import { WithdrawalsDetailModal } from '@library/components/modals/WithdrawalsDetailModal/WithdrawalsDetailModal';
import type { WithdrawalsFilters } from '@library/types/filters';
import { WithdrawalsHeader } from '@library/components/withdrawals/WithdrawalsHeader';
import { WithdrawalsRows } from '@bo-components/withdrawals/WithdrawalsRows';
import { useState } from 'react';

export interface WithdrawalsTableProps {
  data: PaymentsAPIResponse;
  filters: WithdrawalsFilters;
  fromDate: string;
  toDate: string;
}

export const WithdrawalsTable = ({
  data,
  filters,
  fromDate,
  toDate
}: WithdrawalsTableProps) => {
  const [sort, setSort] = useState<WithdrawalSort>({
    sortBy: 'created',
    sortDirection: 'asc'
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedWithdrawal, setSelectedWithdrawal] = useState<
    Withdrawal | undefined
  >(undefined);

  const onCancelModalCallback = () => {
    setSelectedWithdrawal(undefined);
  };

  const handleWithdrawalRowClick = (withdrawals: Withdrawal) => {
    setSelectedWithdrawal(withdrawals);
  };

  let filteredWithdrawals = data.withdrawals;

  filteredWithdrawals = filteredWithdrawals.filter((withdrawal) =>
    isTimeWithinInterval({
      time: getPaymentsDateTime(withdrawal.created),
      start: DateTime.fromISO(fromDate).startOf('day'),
      end: DateTime.fromISO(toDate).endOf('day')
    })
  );

  if (filters.withdrawalStatus !== SELECT_ALL) {
    filteredWithdrawals = filteredWithdrawals.filter(
      (withdrawal) => withdrawal.status === filters.withdrawalStatus
    );
  }
  if (filters.withdrawalMethod !== SELECT_ALL) {
    filteredWithdrawals = filteredWithdrawals.filter(
      (withdrawal) => withdrawal.method === filters.withdrawalMethod
    );
  }

  return (
    <>
      <div className="text-white">Withdrawal</div>
      <WithdrawalsDetailModal
        user={data.user}
        withdrawal={selectedWithdrawal}
        onCancelModal={onCancelModalCallback}
      />
      <table className="w-full">
        <WithdrawalsHeader handleSort={setSort} sort={sort} />
        <WithdrawalsRows
          onWithdrawalRowClick={handleWithdrawalRowClick}
          withdrawals={filteredWithdrawals}
          user={data.user}
        />
      </table>

      <PaginationButtons
        currentPage={currentPage}
        handlePageChange={setCurrentPage}
        totalPages={data?.paginationInfo.total_pages ?? 0}
      />
    </>
  );
};
